import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, UserPlus, Edit, Trash2, Shield, Database, Upload, X, Building2, AlertCircle, Users, Building, FileText, Star } from "lucide-react";
import { api } from "@/App";
import { toast } from "sonner";
import Header from "@/components/Header";
import TopNav from "@/components/TopNav";

function AdminUsuarios({ user, onLogout }) {
  const navigate = useNavigate();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [empresas, setEmpresas] = useState([]);
  const [demandados, setDemandados] = useState([]);
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [loadingDemandados, setLoadingDemandados] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    nombre_completo: "",
    rut: "",
    telefono: "",
    actuacion: "abogado",
    password: "",
    security_answer: "",
    acceso_data_lake: false,
    puede_subir_data_lake: false,
    puede_eliminar_data_lake: false
  });

  // Estados para modales de crear/editar empresas y demandados
  const [showCreateEmpresaDialog, setShowCreateEmpresaDialog] = useState(false);
  const [showEditEmpresaDialog, setShowEditEmpresaDialog] = useState(false);
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  const [empresaFormData, setEmpresaFormData] = useState({
    nombre_legal: "",
    rut: "",
    contacto_principal_nombre: "",
    contacto_principal_email: "",
    contacto_principal_telefono: ""
  });

  const [showCreateDemandadoDialog, setShowCreateDemandadoDialog] = useState(false);
  const [showEditDemandadoDialog, setShowEditDemandadoDialog] = useState(false);
  const [selectedDemandado, setSelectedDemandado] = useState(null);
  const [demandadoFormData, setDemandadoFormData] = useState({
    nombre_legal: "",
    rut: ""
  });

  const [activeTab, setActiveTab] = useState("usuarios");

  useEffect(() => {
    // Verificar que el usuario sea CEO
    if (user.actuacion !== "CEO") {
      toast.error("No tienes permisos para acceder a esta sección");
      navigate("/dashboard");
      return;
    }
    loadUsuarios();
    if (activeTab === "empresas") {
      loadEmpresas();
    } else if (activeTab === "demandados") {
      loadDemandados();
    }
  }, [user, navigate, activeTab]);

  const loadUsuarios = async () => {
    try {
      setLoading(true);
      const response = await api.get("/usuarios");
      setUsuarios(response.data);
    } catch (error) {
      console.error("Error cargando usuarios:", error);
      toast.error("Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!formData.email || !formData.nombre_completo || !formData.rut || !formData.telefono || !formData.actuacion) {
      toast.error("Todos los campos son obligatorios");
      return;
    }

    try {
      await api.post("/usuarios", formData);
      toast.success("Usuario creado exitosamente");
      setShowCreateDialog(false);
      resetForm();
      loadUsuarios();
    } catch (error) {
      console.error("Error creando usuario:", error);
      toast.error(error.response?.data?.detail || "Error al crear usuario");
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();

    try {
      await api.put(`/usuarios/${selectedUser.id}`, {
        nombre_completo: formData.nombre_completo,
        rut: formData.rut,
        telefono: formData.telefono,
        actuacion: formData.actuacion,
        acceso_data_lake: formData.acceso_data_lake,
        puede_subir_data_lake: formData.puede_subir_data_lake,
        puede_eliminar_data_lake: formData.puede_eliminar_data_lake
      });
      toast.success("Usuario actualizado exitosamente");
      setShowEditDialog(false);
      setSelectedUser(null);
      resetForm();
      loadUsuarios();
    } catch (error) {
      console.error("Error actualizando usuario:", error);
      toast.error(error.response?.data?.detail || "Error al actualizar usuario");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("¿Está seguro que desea eliminar este usuario? Esta acción no se puede deshacer.")) {
      return;
    }

    try {
      await api.delete(`/usuarios/${userId}`);
      toast.success("Usuario eliminado exitosamente");
      loadUsuarios();
    } catch (error) {
      console.error("Error eliminando usuario:", error);
      toast.error(error.response?.data?.detail || "Error al eliminar usuario");
    }
  };

  const loadEmpresas = async () => {
    setLoadingEmpresas(true);
    try {
      const response = await api.get('/admin/empresas-clientes');
      setEmpresas(response.data);
    } catch (error) {
      console.error('Error cargando empresas:', error);
      toast.error('Error al cargar empresas');
    } finally {
      setLoadingEmpresas(false);
    }
  };

  const loadDemandados = async () => {
    setLoadingDemandados(true);
    try {
      const response = await api.get('/admin/demandados');
      setDemandados(response.data);
    } catch (error) {
      console.error('Error cargando demandados:', error);
      toast.error('Error al cargar demandados');
    } finally {
      setLoadingDemandados(false);
    }
  };

  const handleDeleteEmpresa = async (empresaId, nombre) => {
    if (!window.confirm(`¿Eliminar empresa "${nombre}"?`)) return;
    
    try {
      await api.delete(`/admin/empresas-clientes/${empresaId}`);
      toast.success('Empresa eliminada');
      loadEmpresas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar empresa');
    }
  };

  const handleDeleteDemandado = async (demandadoId, nombre) => {
    if (!window.confirm(`¿Eliminar demandado "${nombre}"?`)) return;
    
    try {
      await api.delete(`/admin/demandados/${demandadoId}`);
      toast.success('Demandado eliminado');
      loadDemandados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar demandado');
    }
  };

  const openEditDialog = (usuario) => {
    setSelectedUser(usuario);
    setFormData({
      email: usuario.email,
      nombre_completo: usuario.nombre_completo || usuario.name,
      rut: usuario.rut || "",
      telefono: usuario.telefono || "",
      actuacion: usuario.actuacion || "abogado",
      acceso_data_lake: usuario.acceso_data_lake || false,
      puede_subir_data_lake: usuario.puede_subir_data_lake || false,
      puede_eliminar_data_lake: usuario.puede_eliminar_data_lake || false
    });
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setFormData({
      email: "",
      nombre_completo: "",
      rut: "",
      telefono: "",
      actuacion: "abogado",
      password: "",
      security_answer: "",
      acceso_data_lake: false,
      puede_subir_data_lake: false,
      puede_eliminar_data_lake: false
    });
  };

  const getRolLabel = (actuacion) => {
    const roles = {
      "CEO": "CEO",
      "gerente": "Gerente",
      "abogado": "Abogado",
      "abogado_litigante": "Abogado Litigante",
      "testigo": "Testigo"
    };
    return roles[actuacion] || actuacion;
  };

  // ===== FUNCIONES PARA EMPRESAS =====
  const handleCreateEmpresa = async (e) => {
    e.preventDefault();
    if (!empresaFormData.nombre_legal || !empresaFormData.rut || !empresaFormData.contacto_principal_nombre || !empresaFormData.contacto_principal_email || !empresaFormData.contacto_principal_telefono) {
      toast.error("Todos los campos son obligatorios");
      return;
    }
    try {
      await api.post("/admin/empresas-clientes", empresaFormData);
      toast.success("Empresa creada exitosamente");
      setShowCreateEmpresaDialog(false);
      resetEmpresaForm();
      loadEmpresas();
    } catch (error) {
      console.error("Error creando empresa:", error);
      toast.error(error.response?.data?.detail || "Error al crear empresa");
    }
  };

  const handleUpdateEmpresa = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/empresas-clientes/${selectedEmpresa.id}`, empresaFormData);
      toast.success("Empresa actualizada exitosamente");
      setShowEditEmpresaDialog(false);
      setSelectedEmpresa(null);
      resetEmpresaForm();
      loadEmpresas();
    } catch (error) {
      console.error("Error actualizando empresa:", error);
      toast.error(error.response?.data?.detail || "Error al actualizar empresa");
    }
  };

  const openEditEmpresaDialog = (empresa) => {
    setSelectedEmpresa(empresa);
    setEmpresaFormData({
      nombre_legal: empresa.nombre_legal,
      rut: empresa.rut,
      contacto_principal_nombre: empresa.contacto_principal_nombre,
      contacto_principal_email: empresa.contacto_principal_email,
      contacto_principal_telefono: empresa.contacto_principal_telefono
    });
    setShowEditEmpresaDialog(true);
  };

  const resetEmpresaForm = () => {
    setEmpresaFormData({
      nombre_legal: "",
      rut: "",
      contacto_principal_nombre: "",
      contacto_principal_email: "",
      contacto_principal_telefono: ""
    });
  };

  // ===== FUNCIONES PARA DEMANDADOS =====
  const handleCreateDemandado = async (e) => {
    e.preventDefault();
    if (!demandadoFormData.nombre_legal) {
      toast.error("El nombre legal es obligatorio");
      return;
    }
    try {
      await api.post("/demandados", demandadoFormData);
      toast.success("Demandado creado exitosamente");
      setShowCreateDemandadoDialog(false);
      resetDemandadoForm();
      loadDemandados();
    } catch (error) {
      console.error("Error creando demandado:", error);
      toast.error(error.response?.data?.detail || "Error al crear demandado");
    }
  };

  const handleUpdateDemandado = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/demandados/${selectedDemandado.id}`, demandadoFormData);
      toast.success("Demandado actualizado exitosamente");
      setShowEditDemandadoDialog(false);
      setSelectedDemandado(null);
      resetDemandadoForm();
      loadDemandados();
    } catch (error) {
      console.error("Error actualizando demandado:", error);
      toast.error(error.response?.data?.detail || "Error al actualizar demandado");
    }
  };

  const openEditDemandadoDialog = (demandado) => {
    setSelectedDemandado(demandado);
    setDemandadoFormData({
      nombre_legal: demandado.nombre_legal,
      rut: demandado.rut || ""
    });
    setShowEditDemandadoDialog(true);
  };

  const resetDemandadoForm = () => {
    setDemandadoFormData({
      nombre_legal: "",
      rut: ""
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tesla-red"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      {/* Header */}
      <TopNav user={user} onLogout={onLogout || (() => { localStorage.removeItem('token'); window.location.href='/'; })} />

      <main className="container mx-auto px-6 py-8">
        <Button
          variant="outline"
          onClick={() => navigate("/dashboard")}
          className="mb-6 border-tesla-grayLight"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver al Dashboard
        </Button>

        <div className="mb-8">
          <h2 className="text-3xl font-bold text-tesla-black">Administración del Sistema</h2>
          <p className="text-tesla-grayMedium mt-1">Gestiona usuarios, empresas clientes y demandados</p>
        </div>

        {/* Tabs para Usuarios, Empresas y Demandados */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="usuarios" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Usuarios
            </TabsTrigger>
            <TabsTrigger value="empresas" className="flex items-center gap-2">
              <Building className="h-4 w-4" />
              Demandantes
            </TabsTrigger>
            <TabsTrigger value="demandados" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Demandados
            </TabsTrigger>
          </TabsList>

          {/* TAB: USUARIOS */}
          <TabsContent value="usuarios">
            <Card className="border-tesla-grayLight">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Usuarios Registrados</CardTitle>
                    <CardDescription>
                      Solo los usuarios listados aquí pueden iniciar sesión en la plataforma
                    </CardDescription>
                  </div>
                  <Button onClick={() => setShowCreateDialog(true)} className="bg-tesla-red hover:bg-red-700 text-white">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Crear Usuario
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Nombre Completo</TableHead>
                  <TableHead>RUT</TableHead>
                  <TableHead>Rol/Actuación</TableHead>
                  <TableHead className="text-center">Permisos Data Lake</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usuarios.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                      No hay usuarios registrados
                    </TableCell>
                  </TableRow>
                ) : (
                  usuarios.map((usuario) => (
                    <TableRow key={usuario.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {usuario.email === "mpino@allcom.cl" && (
                            <Star className="h-4 w-4 fill-yellow-400 text-yellow-500" title="CEO principal" data-testid="primary-ceo-star" />
                          )}
                          <span>{usuario.email}</span>
                          {usuario.actuacion === "CEO" && usuario.email !== "mpino@allcom.cl" && (
                            <Shield className="h-4 w-4 text-blue-600" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{usuario.nombre_completo || usuario.name}</TableCell>
                      <TableCell>{usuario.rut || "-"}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          usuario.actuacion === "CEO" ? "bg-blue-100 text-blue-800" :
                          usuario.actuacion === "gerente" ? "bg-green-100 text-green-800" :
                          "bg-gray-100 text-gray-800"
                        }`}>
                          {getRolLabel(usuario.actuacion)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        {usuario.email === "mpino@allcom.cl" ? (
                          <div className="flex items-center justify-center gap-2">
                            <span className="text-xs font-semibold text-blue-600">CEO (TOTAL)</span>
                          </div>
                        ) : (usuario.acceso_data_lake || usuario.puede_subir_data_lake || usuario.puede_eliminar_data_lake) ? (
                          <div className="flex items-center justify-center gap-1">
                            {usuario.acceso_data_lake && (
                              <Database className="h-4 w-4 text-green-600" title="Ver" />
                            )}
                            {usuario.puede_subir_data_lake && (
                              <Upload className="h-4 w-4 text-blue-600" title="Subir" />
                            )}
                            {usuario.puede_eliminar_data_lake && (
                              <X className="h-4 w-4 text-red-600" title="Eliminar" />
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">Sin acceso</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditDialog(usuario)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          {usuario.actuacion !== "CEO" && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteUser(usuario.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
            </Card>
          </TabsContent>

          {/* TAB: EMPRESAS CLIENTES */}
          <TabsContent value="empresas">
            <Card className="border-tesla-grayLight">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Demandantes</CardTitle>
                    <CardDescription>
                      Gestiona los demandantes (empresas o personas naturales)
                    </CardDescription>
                  </div>
                  <Button onClick={() => setShowCreateEmpresaDialog(true)} className="bg-tesla-red hover:bg-red-700 text-white">
                    <Building className="h-4 w-4 mr-2" />
                    Crear Demandante
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingEmpresas ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tesla-red"></div>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nombre Legal</TableHead>
                        <TableHead>RUT</TableHead>
                        <TableHead>Contacto Principal</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Teléfono</TableHead>
                        <TableHead className="text-center">Casos</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {empresas.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                            No hay empresas registradas
                          </TableCell>
                        </TableRow>
                      ) : (
                        empresas.map((empresa) => (
                          <TableRow key={empresa.id}>
                            <TableCell className="font-medium">{empresa.nombre_legal}</TableCell>
                            <TableCell>{empresa.rut}</TableCell>
                            <TableCell>{empresa.contacto_principal_nombre}</TableCell>
                            <TableCell>{empresa.contacto_principal_email}</TableCell>
                            <TableCell>{empresa.contacto_principal_telefono}</TableCell>
                            <TableCell className="text-center">
                              {empresa.tiene_casos ? (
                                <AlertCircle className="h-4 w-4 text-yellow-600 mx-auto" title="Tiene casos asociados" />
                              ) : (
                                <span className="text-gray-400 text-xs">Sin casos</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openEditEmpresaDialog(empresa)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDeleteEmpresa(empresa.id, empresa.nombre_legal)}
                                  className="text-red-600 hover:text-red-700"
                                  disabled={empresa.tiene_casos}
                                  title={empresa.tiene_casos ? "No se puede eliminar: tiene casos asociados" : "Eliminar empresa"}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB: DEMANDADOS */}
          <TabsContent value="demandados">
            <Card className="border-tesla-grayLight">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Demandados</CardTitle>
                    <CardDescription>
                      Gestiona las entidades o personas que son demandadas en los casos
                    </CardDescription>
                  </div>
                  <Button onClick={() => setShowCreateDemandadoDialog(true)} className="bg-tesla-red hover:bg-red-700 text-white">
                    <Building2 className="h-4 w-4 mr-2" />
                    Crear Demandado
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingDemandados ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tesla-red"></div>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nombre Legal</TableHead>
                        <TableHead>RUT</TableHead>
                        <TableHead className="text-center">Casos</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {demandados.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                            No hay demandados registrados
                          </TableCell>
                        </TableRow>
                      ) : (
                        demandados.map((demandado) => (
                          <TableRow key={demandado.id}>
                            <TableCell className="font-medium">{demandado.nombre_legal}</TableCell>
                            <TableCell>{demandado.rut || "-"}</TableCell>
                            <TableCell className="text-center">
                              {demandado.tiene_casos ? (
                                <AlertCircle className="h-4 w-4 text-yellow-600 mx-auto" title="Tiene casos asociados" />
                              ) : (
                                <span className="text-gray-400 text-xs">Sin casos</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openEditDemandadoDialog(demandado)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDeleteDemandado(demandado.id, demandado.nombre_legal)}
                                  className="text-red-600 hover:text-red-700"
                                  disabled={demandado.tiene_casos}
                                  title={demandado.tiene_casos ? "No se puede eliminar: tiene casos asociados" : "Eliminar demandado"}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Dialog: Crear Usuario */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Crear Nuevo Usuario</DialogTitle>
              <DialogDescription>
                Complete los datos del usuario que tendrá acceso a la plataforma
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUser}>
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="usuario@allcom.cl"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="nombre_completo">Nombre Completo *</Label>
                  <Input
                    id="nombre_completo"
                    value={formData.nombre_completo}
                    onChange={(e) => setFormData({ ...formData, nombre_completo: e.target.value })}
                    placeholder="Juan Pérez García"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="rut">RUT *</Label>
                  <Input
                    id="rut"
                    value={formData.rut}
                    onChange={(e) => setFormData({ ...formData, rut: e.target.value })}
                    placeholder="12.345.678-9"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="telefono">Teléfono *</Label>
                  <Input
                    id="telefono"
                    value={formData.telefono}
                    onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                    placeholder="+56 9 1234 5678"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="actuacion">Rol/Actuación *</Label>
                  <Select
                    value={formData.actuacion}
                    onValueChange={(value) => setFormData({ ...formData, actuacion: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CEO">CEO</SelectItem>
                      <SelectItem value="gerente">Gerente</SelectItem>
                      <SelectItem value="abogado">Abogado</SelectItem>
                      <SelectItem value="abogado_litigante">Abogado Litigante</SelectItem>
                      <SelectItem value="testigo">Testigo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-3 pt-2 border-t">
                  <Label className="text-sm font-semibold">Seguridad</Label>
                  <div>
                    <Label htmlFor="password">Contraseña inicial</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Dejar vacío para generar temporal"
                    />
                    <p className="text-xs text-gray-500 mt-1">Si no se ingresa, se generará una contraseña temporal</p>
                  </div>
                  <div>
                    <Label htmlFor="security_answer">Respuesta de seguridad</Label>
                    <Input
                      id="security_answer"
                      type="text"
                      value={formData.security_answer}
                      onChange={(e) => setFormData({ ...formData, security_answer: e.target.value })}
                      placeholder="Nombre de tu primera mascota"
                    />
                    <p className="text-xs text-gray-500 mt-1">Pregunta: ¿Cuál es el nombre de tu primera mascota?</p>
                  </div>
                </div>
                <div className="space-y-3 pt-2 border-t">
                  <Label className="text-sm font-semibold">Permisos Data Lake</Label>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="acceso_data_lake" 
                      checked={formData.acceso_data_lake}
                      onCheckedChange={(checked) => setFormData({ ...formData, acceso_data_lake: checked })}
                    />
                    <Label htmlFor="acceso_data_lake" className="cursor-pointer flex items-center gap-2">
                      <Database className="h-4 w-4 text-green-600" />
                      Ver documentos
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="puede_subir_data_lake" 
                      checked={formData.puede_subir_data_lake}
                      onCheckedChange={(checked) => setFormData({ ...formData, puede_subir_data_lake: checked })}
                    />
                    <Label htmlFor="puede_subir_data_lake" className="cursor-pointer flex items-center gap-2">
                      <Upload className="h-4 w-4 text-blue-600" />
                      Subir documentos
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="puede_eliminar_data_lake" 
                      checked={formData.puede_eliminar_data_lake}
                      onCheckedChange={(checked) => setFormData({ ...formData, puede_eliminar_data_lake: checked })}
                    />
                    <Label htmlFor="puede_eliminar_data_lake" className="cursor-pointer flex items-center gap-2">
                      <X className="h-4 w-4 text-red-600" />
                      Eliminar documentos
                    </Label>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => {
                  setShowCreateDialog(false);
                  resetForm();
                }}>
                  Cancelar
                </Button>
                <Button type="submit">Crear Usuario</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog: Editar Usuario */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Editar Usuario</DialogTitle>
              <DialogDescription>
                Modifique los datos del usuario
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateUser}>
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="edit_email">Email</Label>
                  <Input
                    id="edit_email"
                    type="email"
                    value={formData.email}
                    disabled
                    className="bg-gray-100"
                  />
                  <p className="text-xs text-gray-500 mt-1">El email no se puede modificar</p>
                </div>
                <div>
                  <Label htmlFor="edit_nombre_completo">Nombre Completo *</Label>
                  <Input
                    id="edit_nombre_completo"
                    value={formData.nombre_completo}
                    onChange={(e) => setFormData({ ...formData, nombre_completo: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="edit_rut">RUT *</Label>
                  <Input
                    id="edit_rut"
                    value={formData.rut}
                    onChange={(e) => setFormData({ ...formData, rut: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="edit_telefono">Teléfono *</Label>
                  <Input
                    id="edit_telefono"
                    value={formData.telefono}
                    onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="edit_actuacion">Rol/Actuación *</Label>
                  <Select
                    value={formData.actuacion}
                    onValueChange={(value) => setFormData({ ...formData, actuacion: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CEO">CEO</SelectItem>
                      <SelectItem value="gerente">Gerente</SelectItem>
                      <SelectItem value="abogado">Abogado</SelectItem>
                      <SelectItem value="abogado_litigante">Abogado Litigante</SelectItem>
                      <SelectItem value="testigo">Testigo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-3 pt-2 border-t">
                  <Label className="text-sm font-semibold">Permisos Data Lake</Label>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="edit_acceso_data_lake" 
                      checked={formData.acceso_data_lake}
                      onCheckedChange={(checked) => setFormData({ ...formData, acceso_data_lake: checked })}
                    />
                    <Label htmlFor="edit_acceso_data_lake" className="cursor-pointer flex items-center gap-2">
                      <Database className="h-4 w-4 text-green-600" />
                      Ver documentos
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="edit_puede_subir_data_lake" 
                      checked={formData.puede_subir_data_lake}
                      onCheckedChange={(checked) => setFormData({ ...formData, puede_subir_data_lake: checked })}
                    />
                    <Label htmlFor="edit_puede_subir_data_lake" className="cursor-pointer flex items-center gap-2">
                      <Upload className="h-4 w-4 text-blue-600" />
                      Subir documentos
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="edit_puede_eliminar_data_lake" 
                      checked={formData.puede_eliminar_data_lake}
                      onCheckedChange={(checked) => setFormData({ ...formData, puede_eliminar_data_lake: checked })}
                    />
                    <Label htmlFor="edit_puede_eliminar_data_lake" className="cursor-pointer flex items-center gap-2">
                      <X className="h-4 w-4 text-red-600" />
                      Eliminar documentos
                    </Label>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => {
                  setShowEditDialog(false);
                  setSelectedUser(null);
                  resetForm();
                }}>
                  Cancelar
                </Button>
                <Button type="submit">Guardar Cambios</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog: Crear Empresa */}
        <Dialog open={showCreateEmpresaDialog} onOpenChange={setShowCreateEmpresaDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Crear Nueva Empresa Cliente</DialogTitle>
              <DialogDescription>
                Complete los datos de la empresa cliente
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateEmpresa}>
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="empresa_nombre_legal">Nombre Legal *</Label>
                  <Input
                    id="empresa_nombre_legal"
                    value={empresaFormData.nombre_legal}
                    onChange={(e) => setEmpresaFormData({ ...empresaFormData, nombre_legal: e.target.value })}
                    placeholder="Acme Corporation SpA"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="empresa_rut">RUT *</Label>
                  <Input
                    id="empresa_rut"
                    value={empresaFormData.rut}
                    onChange={(e) => setEmpresaFormData({ ...empresaFormData, rut: e.target.value })}
                    placeholder="12.345.678-9"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="empresa_contacto_nombre">Contacto Principal - Nombre *</Label>
                  <Input
                    id="empresa_contacto_nombre"
                    value={empresaFormData.contacto_principal_nombre}
                    onChange={(e) => setEmpresaFormData({ ...empresaFormData, contacto_principal_nombre: e.target.value })}
                    placeholder="Juan Pérez"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="empresa_contacto_email">Contacto Principal - Email *</Label>
                  <Input
                    id="empresa_contacto_email"
                    type="email"
                    value={empresaFormData.contacto_principal_email}
                    onChange={(e) => setEmpresaFormData({ ...empresaFormData, contacto_principal_email: e.target.value })}
                    placeholder="contacto@empresa.cl"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="empresa_contacto_telefono">Contacto Principal - Teléfono *</Label>
                  <Input
                    id="empresa_contacto_telefono"
                    value={empresaFormData.contacto_principal_telefono}
                    onChange={(e) => setEmpresaFormData({ ...empresaFormData, contacto_principal_telefono: e.target.value })}
                    placeholder="+56 9 1234 5678"
                    required
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => {
                  setShowCreateEmpresaDialog(false);
                  resetEmpresaForm();
                }}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-tesla-red hover:bg-red-700 text-white">Crear Empresa</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog: Editar Empresa */}
        <Dialog open={showEditEmpresaDialog} onOpenChange={setShowEditEmpresaDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Editar Empresa Cliente</DialogTitle>
              <DialogDescription>
                Modifique los datos de la empresa
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateEmpresa}>
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="edit_empresa_nombre_legal">Nombre Legal *</Label>
                  <Input
                    id="edit_empresa_nombre_legal"
                    value={empresaFormData.nombre_legal}
                    onChange={(e) => setEmpresaFormData({ ...empresaFormData, nombre_legal: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="edit_empresa_rut">RUT *</Label>
                  <Input
                    id="edit_empresa_rut"
                    value={empresaFormData.rut}
                    onChange={(e) => setEmpresaFormData({ ...empresaFormData, rut: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="edit_empresa_contacto_nombre">Contacto Principal - Nombre *</Label>
                  <Input
                    id="edit_empresa_contacto_nombre"
                    value={empresaFormData.contacto_principal_nombre}
                    onChange={(e) => setEmpresaFormData({ ...empresaFormData, contacto_principal_nombre: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="edit_empresa_contacto_email">Contacto Principal - Email *</Label>
                  <Input
                    id="edit_empresa_contacto_email"
                    type="email"
                    value={empresaFormData.contacto_principal_email}
                    onChange={(e) => setEmpresaFormData({ ...empresaFormData, contacto_principal_email: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="edit_empresa_contacto_telefono">Contacto Principal - Teléfono *</Label>
                  <Input
                    id="edit_empresa_contacto_telefono"
                    value={empresaFormData.contacto_principal_telefono}
                    onChange={(e) => setEmpresaFormData({ ...empresaFormData, contacto_principal_telefono: e.target.value })}
                    required
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => {
                  setShowEditEmpresaDialog(false);
                  setSelectedEmpresa(null);
                  resetEmpresaForm();
                }}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-tesla-red hover:bg-red-700 text-white">Guardar Cambios</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog: Crear Demandado */}
        <Dialog open={showCreateDemandadoDialog} onOpenChange={setShowCreateDemandadoDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Crear Nuevo Demandado</DialogTitle>
              <DialogDescription>
                Complete los datos del demandado
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateDemandado}>
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="demandado_nombre_legal">Nombre Legal *</Label>
                  <Input
                    id="demandado_nombre_legal"
                    value={demandadoFormData.nombre_legal}
                    onChange={(e) => setDemandadoFormData({ ...demandadoFormData, nombre_legal: e.target.value })}
                    placeholder="Empresa o Persona Demandada"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="demandado_rut">RUT (Opcional)</Label>
                  <Input
                    id="demandado_rut"
                    value={demandadoFormData.rut}
                    onChange={(e) => setDemandadoFormData({ ...demandadoFormData, rut: e.target.value })}
                    placeholder="12.345.678-9"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => {
                  setShowCreateDemandadoDialog(false);
                  resetDemandadoForm();
                }}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-tesla-red hover:bg-red-700 text-white">Crear Demandado</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog: Editar Demandado */}
        <Dialog open={showEditDemandadoDialog} onOpenChange={setShowEditDemandadoDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Editar Demandado</DialogTitle>
              <DialogDescription>
                Modifique los datos del demandado
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateDemandado}>
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="edit_demandado_nombre_legal">Nombre Legal *</Label>
                  <Input
                    id="edit_demandado_nombre_legal"
                    value={demandadoFormData.nombre_legal}
                    onChange={(e) => setDemandadoFormData({ ...demandadoFormData, nombre_legal: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="edit_demandado_rut">RUT (Opcional)</Label>
                  <Input
                    id="edit_demandado_rut"
                    value={demandadoFormData.rut}
                    onChange={(e) => setDemandadoFormData({ ...demandadoFormData, rut: e.target.value })}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => {
                  setShowEditDemandadoDialog(false);
                  setSelectedDemandado(null);
                  resetDemandadoForm();
                }}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-tesla-red hover:bg-red-700 text-white">Guardar Cambios</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}

export default AdminUsuarios;
